import React, {useState, useEffect} from 'react';
import axios from 'axios';
import {MapPin, Building, Phone, Mail, Clock, Target, Eye, Star, Zap, Droplets, Globe, Settings, BarChart, GraduationCap} from 'lucide-react';
import './About.css';

const About = () => {
  const [locations, setLocations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingPartners, setIsLoadingPartners] = useState(true);

  // ✨ Animations anime.js
  useEffect(() => {
    if (typeof window.anime !== 'function') return;
    const anime = window.anime;

    anime({
      targets: '.about-hero-content > *',
      opacity: [0, 1],
      translateY: [30, 0],
      delay: anime.stagger(150),
      duration: 1000,
      easing: 'easeOutExpo'
    });

    const handleScroll = () => {
      const elements = document.querySelectorAll('.about-animate-hidden');
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.9;
        if (isVisible && !el.classList.contains('animated')) {
          el.classList.add('animated');
          anime({
            targets: el,
            opacity: [0, 1],
            translateY: [30, 0],
            duration: 800,
            easing: 'easeOutQuad'
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 300);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/locations`);
        setLocations(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/partners?publicOnly=true`);
        setPartners(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setPartners([]);
      } finally {
        setIsLoadingPartners(false);
      }
    };

    fetchLocations();
    fetchPartners();
  }, []);

  return (
    <div className="about" id="about">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-bg"></div>
        <div className="about-hero-content">
          <h1>Innover pour un <span>avenir durable.</span></h1>
          <p>Sastra Energy accompagne la transition énergétique de la Tunisie avec des solutions solaires intelligentes et performantes.</p>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-content about-animate-hidden">
            <div className="story-text">
              <h2>Notre Histoire</h2>
              <p>
                Fondée en 2016, <strong>Sastra Energy</strong> est née de la passion d'une équipe d'experts
                pour les énergies renouvelables. Nous avons commencé avec une vision claire :
                démocratiser l'accès à l'énergie solaire en Tunisie.
              </p>
              <p>
                Aujourd'hui, nous sommes fiers d'être l'un des leaders du secteur, offrant des solutions
                clés en main pour les particuliers, les industries et le secteur agricole.
              </p>
            </div>
            <div className="story-visual">
              <div className="year-highlight">
                <span className="year">2016</span>
                <span className="year-text">Lancement de l'aventure</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="mission-bg-overlay"></div>
        <div className="container">
          <div className="mission-content">
            <div className="mission-grid">
              <div className="mission-card about-animate-hidden">
                <div className="mission-icon"><Target /></div>
                <h3>Notre Mission</h3>
                <p>Déployer des technologies solaires de pointe pour réduire l'empreinte carbone et optimiser les coûts énergétiques de nos clients.</p>
              </div>

              <div className="mission-card about-animate-hidden" style={{transitionDelay: '0.1s'}}>
                <div className="mission-icon"><Eye /></div>
                <h3>Notre Vision</h3>
                <p>Devenir le partenaire énergétique de référence en Tunisie, reconnu pour son innovation et son engagement envers le développement durable.</p>
              </div>

              <div className="mission-card about-animate-hidden" style={{transitionDelay: '0.2s'}}>
                <div className="mission-icon"><Star /></div>
                <h3>Nos Valeurs</h3>
                <p>L'excellence technique, la transparence totale et un service client irréprochable guident chacune de nos installations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="activities-section">
        <div className="container">
          <div className="section-header about-animate-hidden">
            <h2>Nos Domaines d'Expertise</h2>
          </div>

          <div className="activities-grid">
            <div className="activity-card about-animate-hidden">
              <div className="activity-icon"><Zap /></div>
              <h3>Raccordé Réseau BT</h3>
              <p>Solutions résidentielles et tertiaires pour une autonomie énergétique accrue et une réduction immédiate de facture.</p>
              <ul className="activity-features">
                <li>Étude personnalisée</li>
                <li>Installation rapide</li>
              </ul>
            </div>

            <div className="activity-card about-animate-hidden">
              <div className="activity-icon"><Building /></div>
              <h3>Raccordé Réseau MT</h3>
              <p>Centrales solaires industrielles optimisées pour les gros consommateurs d'énergie et les parcs solaires.</p>
              <ul className="activity-features">
                <li>Haute performance</li>
                <li>Suivi en temps réel</li>
              </ul>
            </div>

            <div className="activity-card about-animate-hidden">
              <div className="activity-icon"><Droplets /></div>
              <h3>Pompage Solaire</h3>
              <p>Modernisation des systèmes d'irrigation agricole pour une agriculture plus durable et moins coûteuse.</p>
              <ul className="activity-features">
                <li>Irrigation propre</li>
                <li>Retour sur investissement</li>
              </ul>
            </div>

            <div className="activity-card about-animate-hidden">
              <div className="activity-icon"><Globe /></div>
              <h3>Sites Isolés</h3>
              <p>Électrification de zones reculées avec stockage par batteries pour une énergie disponible 24h/24.</p>
              <ul className="activity-features">
                <li>Systèmes hybrides</li>
                <li>Batteries lithium</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section">
        <div className="container">
          <div className="section-header about-animate-hidden">
            <h2>Ils nous font confiance</h2>
            <h4>Une sélection de partenaires internationaux de premier rang.</h4>
          </div>

          {isLoadingPartners ? (
            <div style={{textAlign: 'center', padding: '40px'}}>Chargement...</div>
          ) : (
            <div className="partners-carousel about-animate-hidden">
              <div className="partners-track">
                {partners.concat(partners).map((partner, index) => (
                  <div key={index} className="partner-slide">
                    <a href={partner.website} target="_blank" rel="noopener noreferrer">
                      <div className="partner-logo-wrapper">
                        {partner.logo && <img src={partner.logo} alt={partner.name} />}
                      </div>
                      <div className="partner-name">{partner.name}</div>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Locations Section Refined */}
      <section className="pg-about-locations-sec" id="locations">
        <div className="container">
          <div className="pg-about-loc-header about-animate-hidden">
            <h2>Proximité & Réactivité</h2>
            <p>Nos bureaux répartis sur le territoire tunisien pour un service local de qualité.</p>
          </div>

          <div className="pg-about-loc-grid">
            {isLoadingLocations ? (
              <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '50px'}}>Chargement des implantations...</div>
            ) : locations.length > 0 ? (
              locations.map((location, index) => (
                <div key={location._id || index} className={`pg-about-loc-card about-animate-hide ${location.isPrimary ? 'primary' : ''}`}>
                  {location.isPrimary && <div className="pg-about-loc-badge">Siège</div>}
                  <div className="pg-about-loc-icon-wrap">
                    <MapPin size={24} />
                  </div>
                  <h3>{location.city}</h3>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    <div style={{display: 'flex', gap: '10px', color: 'var(--text-muted)'}}>
                      <Building size={16} style={{color: 'var(--primary-yellow)', flexShrink: 0}} />
                      <span style={{fontSize: '0.9rem'}}>{location.address}</span>
                    </div>
                    <div style={{display: 'flex', gap: '10px', color: 'var(--text-muted)'}}>
                      <Phone size={16} style={{color: 'var(--primary-yellow)', flexShrink: 0}} />
                      <span style={{fontSize: '0.9rem'}}>+216 {location.contact}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : null}
          </div>


        </div>
      </section>

      {/* Services Section */}
      <section className="services-highlight">
        <div className="container">
          <div className="services-content about-animate-hidden">
            <h2>Accompagnement 360°</h2>
            <div className="services-list">
              <div className="service-item">
                <div className="service-icon"><Settings /></div>
                <div>
                  <h4>Ingénierie & Conseil</h4>
                  <p>Études de faisabilité, audits énergétiques et dimensionnement technique sur mesure.</p>
                </div>
              </div>
              <div className="service-item">
                <div className="service-icon"><BarChart /></div>
                <div>
                  <h4>Suivi de Performance</h4>
                  <p>Monitoring à distance pour garantir que chaque kilowatt-heure est optimisé au maximum.</p>
                </div>
              </div>
              <div className="service-item">
                <div className="service-icon"><GraduationCap /></div>
                <div>
                  <h4>Transfert de Compétences</h4>
                  <p>Formation de vos équipes pour une exploitation intelligente et durable du système.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
